import { useEffect, useState } from 'react';
import { EventBus, type ConversationDTO } from '@campus/core';
import { Button, Tabs } from '../ds/index.js';
import { SidePanel, panelHeaderStyle } from '../people/SidePanel.js';
import { PeopleBrowser } from '../people/PeoplePanel.js';
import { loadMembers, sessionInfo } from '../people/membersStore.js';
import { useMessagingStore, useUnreadTotal, openDirect, refreshConversations } from '../../messaging/store.js';
import { MessagingApiError } from '../../messaging/client.js';
import { ConversationList } from '../messaging/ConversationList.js';
import { ConversationView } from '../messaging/ConversationView.js';
import { NewMessagePicker } from '../messaging/NewMessagePicker.js';
import { GroupCreator } from '../messaging/GroupCreator.js';
import { GroupDetails } from '../messaging/GroupDetails.js';
import { sendErrorLabel } from '../messaging/format.js';

/** Sous-onglet du Chat : un fil partagé (id de la conversation) ou les messages perso. */
const PERSO = 'perso';
type PersoView = { kind: 'list' } | { kind: 'new' } | { kind: 'new-group' } | { kind: 'conversation'; id: string } | { kind: 'group'; id: string };

/** Derniers choix : on les retrouve en rouvrant le panneau. */
let lastSub: string | null = null;
let lastPersoView: PersoView = { kind: 'list' };

const unreadLabel = (n: number) => (n >= 99 ? '99+' : String(n));
const isShared = (c: ConversationDTO) => c.kind === 'general' || c.kind === 'profile';

/**
 * Messagerie - barre pleine hauteur à droite (plein écran sur mobile, SidePanel).
 * Onglet Chat (tasks/messagerie-plan.md), avec un sous-onglet par fil partagé
 * (Général, fil de son profil d'accès, ex. Copycampers ; les admins voient tous
 * les fils) et « Messages perso » (conversations privées et groupes) ; onglet Membres
 * (même liste que la pastille « N en ligne », PeopleBrowser). `dmUserId` :
 * ouvrir directement la conversation privée avec cette personne.
 *
 * Le chat de bulle (proximité) n'est pas ici : ProximityChatPanel, sans persistance.
 */
export function GeneralChat({ onClose, dmUserId = null, onDmOpened }: {
  onClose: () => void;
  dmUserId?: string | null;
  onDmOpened?: () => void;
}) {
  const isGuest = sessionInfo().isGuest;
  const unread = useUnreadTotal();
  const conversations = useMessagingStore((s) => s.conversations);
  const [tab, setTab] = useState<'chat' | 'people'>('chat');
  const [sub, setSubState] = useState<string | null>(lastSub);
  const [persoView, setPersoViewState] = useState<PersoView>(lastPersoView);
  const setSub = (v: string) => { lastSub = v; setSubState(v); };
  const setPersoView = (v: PersoView) => { lastPersoView = v; setPersoViewState(v); };

  useEffect(() => { void loadMembers(); void refreshConversations(); }, []);

  const shared = conversations.filter(isShared);
  const personalUnread = conversations.filter((c) => !isShared(c)).reduce((n, c) => n + c.unread, 0);

  // Sous-onglet par défaut : son fil d'accueil (général, ou celui de son profil).
  const fallback = (shared.find((c) => c.home) ?? shared[0])?.id ?? (isGuest ? null : PERSO);
  const current = sub === PERSO && !isGuest ? PERSO
    : sub && shared.some((c) => c.id === sub) ? sub
    : fallback;

  // Conversation perso qui n'est plus lisible (accès changé) : retour à la liste.
  useEffect(() => {
    if ((persoView.kind === 'conversation' || persoView.kind === 'group') && conversations.length > 0 && !conversations.some((c) => c.id === persoView.id)) {
      setPersoView({ kind: 'list' });
    }
  }, [conversations, persoView]);

  const openWith = async (userId: string) => {
    try {
      const id = await openDirect(userId);
      setTab('chat');
      setSub(PERSO);
      setPersoView({ kind: 'conversation', id });
    } catch (err) {
      const code = err instanceof MessagingApiError ? err.code : 'NETWORK';
      EventBus.emit('ui:toast', { message: sendErrorLabel(code), tone: 'warning', duration: 6000 });
    }
  };

  useEffect(() => {
    if (!dmUserId) return;
    void openWith(dmUserId).finally(() => onDmOpened?.());
  }, [dmUserId]);

  const subItems = [
    ...shared.map((c) => ({ id: c.id, label: c.unread > 0 ? `${c.title ?? 'Général'} · ${unreadLabel(c.unread)}` : (c.title ?? 'Général') })),
    ...(isGuest ? [] : [{ id: PERSO, label: personalUnread > 0 ? `Messages perso · ${unreadLabel(personalUnread)}` : 'Messages perso' }]),
  ];

  let body;
  if (tab === 'people') body = <PeopleBrowser onClose={onClose} />;
  else if (!current) body = <p className="pl-help" style={{ margin: 16 }}>Chargement…</p>;
  else if (current !== PERSO) body = <ConversationView key={current} conversationId={current} />;
  else if (persoView.kind === 'conversation') {
    const { id } = persoView;
    body = <ConversationView key={id} conversationId={id} onBack={() => setPersoView({ kind: 'list' })} onInfo={() => setPersoView({ kind: 'group', id })} />;
  } else if (persoView.kind === 'group') {
    const { id } = persoView;
    body = <GroupDetails key={id} conversationId={id} onBack={() => setPersoView({ kind: 'conversation', id })} onLeft={() => setPersoView({ kind: 'list' })} />;
  } else if (persoView.kind === 'new-group') {
    body = <GroupCreator onCreated={(id) => setPersoView({ kind: 'conversation', id })} onCancel={() => setPersoView({ kind: 'list' })} />;
  } else if (persoView.kind === 'new') {
    body = <NewMessagePicker onPick={(userId) => { void openWith(userId); }} onCancel={() => setPersoView({ kind: 'list' })} />;
  } else {
    body = (
      <ConversationList onOpen={(id) => setPersoView({ kind: 'conversation', id })}
        onNew={() => setPersoView({ kind: 'new' })} onNewGroup={() => setPersoView({ kind: 'new-group' })} />
    );
  }

  return (
    <SidePanel
      label="Messagerie"
      onClose={onClose}
      header={(
        <header style={{ ...panelHeaderStyle, flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <h2 style={{ margin: 0, font: '700 18px/1.25 var(--font-display)' }}>Messagerie</h2>
            <Button variant="ghost" icon="x" aria-label="Fermer la messagerie" onClick={onClose} />
          </div>
          <div className="pl-tabs-fit"><Tabs
            aria-label="Messagerie"
            items={[
              { id: 'chat', label: unread > 0 ? `Chat · ${unreadLabel(unread)}` : 'Chat' },
              { id: 'people', label: 'Membres' },
            ]}
            value={tab}
            onChange={(id) => setTab(id as 'chat' | 'people')}
          /></div>
          {tab === 'chat' && current && subItems.length > 1 && (
            <div className="pl-tabs-fit"><Tabs
              aria-label="Fils du chat"
              items={subItems}
              value={current}
              onChange={setSub}
            /></div>
          )}
        </header>
      )}
    >
      {body}
    </SidePanel>
  );
}
